import { forwardRef, useState, useEffect } from 'react';
import type { InputHTMLAttributes } from 'react';
import clsx from 'clsx';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
}

// Helper functions for number formatting
function formatNumberWithCommas(value: string): string {
  const cleanValue = value.replace(/[^\d.-]/g, '');
  if (!cleanValue || cleanValue === '-') return cleanValue;

  const parts = cleanValue.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
}

function removeCommas(value: string): string {
  return value.replace(/,/g, '');
}

// Brand colors - exact match with elka-manager
const BRAND_BLUE = '#3C5FA8';
const ERROR_RED = '#f43f5e';

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      className,
      type,
      value,
      onChange,
      onBlur,
      onFocus,
      style,
      ...props
    },
    ref
  ) => {
    const [displayValue, setDisplayValue] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const isNumberType = type === 'number';

    // Sync display value with prop value
    useEffect(() => {
      if (isNumberType && value !== undefined && value !== '') {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- sync controlled external value to formatted display
        setDisplayValue(formatNumberWithCommas(String(value)));
      } else if (value !== undefined) {
        setDisplayValue(String(value));
      }
    }, [value, isNumberType]);

    // Prevent scroll from changing number values
    const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
      if (isNumberType) {
        e.currentTarget.blur();
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isNumberType) {
        const inputValue = e.target.value;
        const cleanValue = removeCommas(inputValue);

        // Update display with formatted value
        setDisplayValue(formatNumberWithCommas(inputValue));

        // Pass clean numeric value to parent
        if (onChange) {
          const syntheticEvent = {
            ...e,
            target: {
              ...e.target,
              value: cleanValue,
            },
          };
          onChange(syntheticEvent as React.ChangeEvent<HTMLInputElement>);
        }
      } else {
        setDisplayValue(e.target.value);
        if (onChange) {
          onChange(e);
        }
      }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      if (isNumberType) {
        const cleanValue = removeCommas(e.target.value);
        setDisplayValue(cleanValue ? formatNumberWithCommas(cleanValue) : '');
      }
      if (onBlur) {
        onBlur(e);
      }
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      if (onFocus) {
        onFocus(e);
      }
    };

    // Focus ring via inline style - guaranteed to work across all apps
    const focusRingColor = error ? ERROR_RED : BRAND_BLUE;
    const inputStyle: React.CSSProperties = {
      transition: 'border-color 0.2s ease, box-shadow 0.25s cubic-bezier(0.22, 1, 0.36, 1)',
      ...(isFocused
        ? {
            boxShadow: `0 0 0 2px ${focusRingColor}`,
            borderColor: 'transparent',
            outline: 'none',
          }
        : {}),
      ...style,
    };

    return (
      <div className="w-full">
        {label && (
          <label className="mb-1 block text-sm font-medium text-gray-700">
            {label}
            {props.required && <span className="mr-1 text-rose-500">*</span>}
          </label>
        )}
        <input
          ref={ref}
          type={isNumberType ? 'text' : type}
          inputMode={isNumberType ? 'decimal' : undefined}
          value={isNumberType ? displayValue : value}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          className={clsx(
            'min-h-[44px] w-full rounded-xl border px-3 py-2.5 md:min-h-0 md:py-2',
            'focus:outline-none',
            'disabled:cursor-not-allowed disabled:bg-gray-100',
            'text-base', // Prevents zoom on iOS
            error ? 'border-rose-300' : 'border-[#E2E8F0] hover:border-[#C7D7F0]',
            className
          )}
          style={inputStyle}
          onWheel={handleWheel}
          {...props}
        />
        {error && <p className="mt-1 text-sm text-rose-600">{error}</p>}
        {helperText && !error && <p className="mt-1 text-sm text-gray-500">{helperText}</p>}
      </div>
    );
  }
);

Input.displayName = 'Input';
